import { c as _c } from "react/compiler-runtime";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import { shallowEqual } from "@xstate/react";
import { useTranslations } from "next-intl";
import { useAppContext } from "@/app/contextProviders/app-context";
import { useSchedulerActor, useSchedulerSelector } from "@/lib/hooks/useSchedulerMachine";
import { getSidebarSteps } from "@/lib/machines/machine-state-mapping";
import type { NavigableStep, SchedulerContext } from "@/types/scheduler-machine";
import { StepItem } from "./StepItem";
import styles from "./Stepper.module.css";
const selectCompletedSteps = (snapshot: any) => snapshot.context.completedSteps as Partial<Record<NavigableStep, true>>;

// getSidebarSteps returns a freshly-built array of freshly-built objects on every call, so
// reference/shallow equality on the array always fails. Compare the fields that actually affect
// what renders instead, so unrelated context updates (e.g. slots loading) don't re-render the sidebar.
function sidebarStepsEqual(a: ReturnType<typeof getSidebarSteps>, b: ReturnType<typeof getSidebarSteps>) {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  return a.every((step, i) => step.machineState === b[i].machineState && step.stepIndex === b[i].stepIndex && step.showBackButton === b[i].showBackButton);
}
interface StepSidebarProps {
  activeStep: number;
  /** Mobile only — the step content rendered inline */
  children?: React.ReactNode;
}
export function StepSidebar(t0) {
  const $ = _c(26);
  const {
    activeStep,
    children
  } = t0;
  const actorRef = useSchedulerActor();
  const SIDEBAR_STEPS = useSchedulerSelector(_temp, sidebarStepsEqual);
  const completedSteps = useSchedulerSelector(selectCompletedSteps);
  const isBooked = useSchedulerSelector(_temp2);
  const theme = useTheme();
  const {
    deviceType
  } = useAppContext();
  let t1;
  if ($[0] !== theme.breakpoints) {
    t1 = theme.breakpoints.down("sm");
    $[0] = theme.breakpoints;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = deviceType === "mobile";
  let t3;
  if ($[2] !== t2) {
    t3 = {
      defaultMatches: t2
    };
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const isSmallScreen = useMediaQuery(t1, t3);
  const context = useSchedulerSelector(_temp3, shallowEqual) as SchedulerContext;
  const t = useTranslations("StepperLabels");
  let t4;
  if ($[4] !== SIDEBAR_STEPS || $[5] !== activeStep || $[6] !== actorRef || $[7] !== children || $[8] !== completedSteps || $[9] !== context || $[10] !== isBooked || $[11] !== isSmallScreen || $[12] !== t) {
    let t5;
    if ($[14] !== SIDEBAR_STEPS.length || $[15] !== activeStep || $[16] !== actorRef || $[17] !== children || $[18] !== completedSteps || $[19] !== context || $[20] !== isBooked || $[21] !== isSmallScreen || $[22] !== t) {
      t5 = (step, i) => {
        const isActive = activeStep === step.stepIndex;
        const isCompleted = isBooked || completedSteps[step.machineState as NavigableStep] === true;
        const isFuture = !isActive && !isCompleted;
        const isLast = i === SIDEBAR_STEPS.length - 1;
        let stepDataFields;
        const isAppointmentReviewStep = step.machineState === "appointmentReview";
        const isConfirmationStep = step.machineState === "confirmation";
        if (isCompleted && step.machineState) {
          stepDataFields = "getSubtitle" in step ? step.getSubtitle?.(context) : undefined;
        } else {
          stepDataFields = undefined;
        }
        return <StepItem key={step.machineState} label={t(step.label)} stepNumber={step.stepIndex} isActive={isActive} isCompleted={isCompleted} isFuture={isFuture} isLast={isLast} isAppointmentReviewStep={isAppointmentReviewStep} isConfirmationStep={isConfirmationStep} stepDataFields={stepDataFields} disableNavigation={isBooked} onNavigate={() => {
          if (isCompleted && !isBooked) {
            actorRef.send({
              type: "GOTO_STEP",
              step: step.machineState as NavigableStep
            });
          }
        }}>{isSmallScreen && isActive ? children : null}</StepItem>;
      };
      $[14] = SIDEBAR_STEPS.length;
      $[15] = activeStep;
      $[16] = actorRef;
      $[17] = children;
      $[18] = completedSteps;
      $[19] = context;
      $[20] = isBooked;
      $[21] = isSmallScreen;
      $[22] = t;
      $[23] = t5;
    } else {
      t5 = $[23];
    }
    t4 = SIDEBAR_STEPS.map(t5);
    $[4] = SIDEBAR_STEPS;
    $[5] = activeStep;
    $[6] = actorRef;
    $[7] = children;
    $[8] = completedSteps;
    $[9] = context;
    $[10] = isBooked;
    $[11] = isSmallScreen;
    $[12] = t;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[24] !== t4) {
    t5 = <Box className={styles.stepperLeftPanel}>{t4}</Box>;
    $[24] = t4;
    $[25] = t5;
  } else {
    t5 = $[25];
  }
  return t5;
}
function _temp3(s_1) {
  return {
    patientData: s_1.context.patientData,
    appointmentData: s_1.context.appointmentData,
    guarantorData: s_1.context.guarantorData
  };
}
function _temp2(s_0) {
  return s_0.matches("confirmation");
}
function _temp(s) {
  return getSidebarSteps(s);
}